import { DecimalPipe, NgClass } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { I18nService } from '../../core/i18n.service';
import { ResponsiveImageComponent } from '../../shared/responsive-image.component';
import { FortuneCategory, FortuneFaq, FortuneFeatureFacet, FortuneListing, FortuneService } from './fortune.service';
import { defaultListingTemplates, ListingTemplateField, ListingTemplatePosition, SiteConfigService } from '../../core/site-config.service';
import { SeoService } from '../../core/seo.service';
import { FavoriteCollectionWidgetComponent } from '../collections/favorite-collection-widget.component';
import { MarkdownHtmlPipe } from '../../shared/markdown-html.pipe';
import { ThemeListExperienceService } from '../../theme/theme-list-experience.service';

@Component({
  selector: 'app-fortune-widget',
  imports: [DecimalPipe, NgClass, MarkdownHtmlPipe, ResponsiveImageComponent, RouterLink, FavoriteCollectionWidgetComponent],
  template: `
    <section class="fortune-widget">
      <nav class="fortune-breadcrumb">
        <a [routerLink]="i18n.routePath('fortune')">{{ commerce() ? commerceLabel('products') : i18n.t('fortune') }}</a>
        @for (category of breadcrumbCategories(); track category.id) {
          <span>/</span>
          @if (category.id === activeCategory()?.id) {
            <strong>{{ category.title }}</strong>
          } @else {
            <a [routerLink]="i18n.routePath('category', { slug: category.slug })">{{ category.title }}</a>
          }
        }
      </nav>

      @if (!activeCategory()) {
        <header class="panel-head catalog-head">
          <div>
            <p class="eyebrow">{{ commerce() ? commerceLabel('catalog') : i18n.t('fortune') }}</p>
            <h1>{{ commerce() ? commerceLabel('catalogTitle') : (rootSeoCategory()?.title || i18n.t('fortune')) }}</h1>
            <p class="category-subtitle">{{ commerce() ? commerceLabel('catalogLead') : rootSeoCategory()?.description }}</p>
          </div>
        </header>
      }

      @if (!activeCategory() && displayCategories().length) {
        <div class="category-grid" [class.commerce-rail]="commerce() && !!activeCategory()">
          @for (category of displayCategories(); track category.id) {
            <a class="category-card" [routerLink]="i18n.routePath('category', { slug: category.slug })">
              <div class="cover">
                @if (category.imageUrl) {
                  <app-responsive-image
                    [url]="category.imageUrl"
                    [mediaSizes]="category.imageSizes"
                    [alt]="category.title"
                    displaySizes="(max-width: 800px) 88vw, 330px"
                  />
                }
                <span>{{ i18n.t('continue') }}</span>
              </div>
              <div class="card-body">
                <h2>{{ category.title }}</h2>
                    <p>{{ childCount(category.id) ? childCount(category.id) + ' ' + i18n.t('subCategory') : (commerce() ? commerceLabel('viewProducts') : i18n.t('seeReaders')) }}</p>
              </div>
            </a>
          }
        </div>
        @if (commerce() && visibleCategories().length > categoryLimit) {
          <button type="button" class="category-more" (click)="categoriesExpanded.update(value => !value)">
            {{ categoriesExpanded() ? commerceLabel('showLess') : commerceLabel('showAll') }}
          </button>
        }
      }

      @if (!activeCategory() && listings().length) {
        <section class="panel root-products" aria-label="Products">
          <div class="panel-head"><div><p class="eyebrow">Products</p><h2>Latest products</h2></div></div>
          <div class="listing-grid seller-focused" [id]="categoryModelAnchor()">
            @for (listing of listings(); track listing.id) {
              <a class="listing-card" [routerLink]="i18n.routePath('listing', { slug: listing.slug })">
                <div class="cover product-cover">
                  @if (listing.imageUrl) { <app-responsive-image [url]="listing.imageUrl" [mediaSizes]="listing.imageSizes" [alt]="listing.title" displaySizes="(max-width: 800px) 88vw, 330px" /> }
                </div>
                <div class="card-body"><h2>{{ listing.title }}</h2><strong>{{ listing.price | number:'1.0-2' }} {{ priceUnit(listing.currency) }}</strong></div>
              </a>
            }
          </div>
        </section>
      }

      @if (activeCategory()) {
        <div class="panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">{{ commerce() ? commerceLabel('products') : i18n.t('readersTitle') }}</p>
              <h1>{{ activeCategory()?.title }}</h1>
              <p class="category-subtitle">{{ commerce() ? categoryIntro() : i18n.t('suitableChoices') }}</p>
              @if(commerce()&&categoryModelHref();as modelHref){<a class="category-compare" [href]="modelHref" (click)="scrollToCategoryModels($event)">{{commerceLabel('compare')}} ↓</a>}
            </div>
            <button type="button" class="ghost" [routerLink]="i18n.routePath('fortune')">{{ commerce() ? commerceLabel('allCategories') : i18n.t('changeCategory') }}</button>
          </div>

          @if (childCategories().length) {
            <div class="subcategory-heading">
              <p class="eyebrow">{{ commerce() ? 'Alt kategoriler' : 'Bu kategoride keşfet' }}</p>
              <p>{{ commerce() ? 'Önce bir alt kategori seçerek ürünleri görüntüle.' : 'İlgilendiğin alt kategoriyi seç.' }}</p>
            </div>
            <div class="category-grid active-subcategory-grid">
              @for (category of displayCategories(); track category.id) {
                <a class="category-card" [routerLink]="i18n.routePath('category', { slug: category.slug })">
                  <div class="cover">
                    @if (category.imageUrl) {
                      <app-responsive-image
                        [url]="category.imageUrl"
                        [mediaSizes]="category.imageSizes"
                        [alt]="category.title"
                        displaySizes="(max-width: 800px) 48vw, 330px"
                      />
                    }
                    <span>{{ i18n.t('continue') }}</span>
                  </div>
                  <div class="card-body">
                    <h2>{{ category.title }}</h2>
                    <p>{{ childCount(category.id) ? childCount(category.id) + ' ' + i18n.t('subCategory') : i18n.t('seeReaders') }}</p>
                  </div>
                </a>
              }
            </div>
          } @else {
          <button type="button" class="filter-open" (click)="filtersOpen.set(true)">{{commerce()?commerceLabel('filter'):'Filtrele'}}</button>
          @if (filtersOpen()) {
            <button type="button" class="filter-backdrop" (click)="filtersOpen.set(false)" [attr.aria-label]="commerceLabel('closeFilters')"></button>
            <aside class="filter-drawer" [attr.aria-label]="commerceLabel('productFilters')">
              <header><strong>{{commerceLabel('filters')}}</strong><button type="button" (click)="filtersOpen.set(false)" [attr.aria-label]="commerceLabel('closeFilters')">×</button></header>
          <div class="listing-filters">
            <div class="filter-row">
              <label><span>{{commerceLabel('search')}}</span><input type="search" [value]="listingSearch()" (input)="listingSearch.set($any($event.target).value)" [placeholder]="commerceLabel('searchProducts')" /></label>
              <label><span>{{commerceLabel('minPrice')}}</span><input type="number" [value]="minPrice()" (input)="minPrice.set($any($event.target).value)" min="0" /></label>
              <label><span>{{commerceLabel('maxPrice')}}</span><input type="number" [value]="maxPrice()" (input)="maxPrice.set($any($event.target).value)" min="0" /></label>
              @if (isFortuneBrand()) { <label><span>Sıralama</span><select [value]="listingSort()" (change)="listingSort.set($any($event.target).value)"><option value="score">Skor</option><option value="rating">Puan</option><option value="orders">Satış</option><option value="reviews">Yorum</option><option value="new">Yeni</option></select></label> }
              @if (hasPhysicalListings()) {
                <label class="filter-check"><input type="checkbox" [checked]="freeShipping()" (change)="freeShipping.set($any($event.target).checked)" /> {{commerceLabel('freeShipping')}}</label>
              }
              <label class="filter-check"><input type="checkbox" [checked]="campaignOnly()" (change)="campaignOnly.set($any($event.target).checked)" /> {{commerceLabel('campaignProducts')}}</label>
              <button type="button" class="ghost" (click)="applyListingFilters()">{{commerceLabel('filter')}}</button>
            </div>
            @if (featureFacets().length) {
              <div class="feature-filters">
                @for (facet of featureFacets(); track facet.id) {
                  <div class="feature-filter">
                    <strong>{{ facet.title }}</strong>
                    <div>
                      @for (value of facet.values; track value.id) {
                        <label class="filter-check filter-check--option">
                          <input type="checkbox" [checked]="isFeatureSelected(facet.id, value.id)" (change)="toggleFeatureValue(facet.id, value.id)" />
                          <span>{{ value.title }}</span><small>{{ value.count }}</small>
                        </label>
                      }
                    </div>
                  </div>
                }
              </div>
            }
            @if (commerce() && sellerFacets().length > 1) {
              <div class="feature-filter seller-filter"><strong>{{commerceLabel('seller')}}</strong><div>
                @for (seller of sellerFacets(); track seller.id) {
                  <label class="filter-check filter-check--option">
                    <input type="checkbox" [checked]="selectedSellerID() === seller.id" (change)="toggleSeller(seller.id)" />
                    <span>{{ seller.name }}</span><small>{{ seller.count }}</small>
                  </label>
                }
              </div></div>
            }
          </div>
            </aside>
          }
          <div class="listing-grid seller-focused" [id]="categoryModelAnchor()">
            @for (listing of listings(); track listing.id) {
              <a class="listing-card" [class.falderyasi-listing-card]="isFortuneBrand()" [attr.data-card-template]="cardTemplateType()" [routerLink]="i18n.routePath('listing', { slug: listing.slug })">
                  <div class="cover seller-cover" [class.product-cover]="commerce()" [class.falderyasi-listing-cover]="isFortuneBrand()">
                  @if (commerce() ? listing.imageUrl : (listing.sellerImage || listing.imageUrl)) {
                    <app-responsive-image
                      [url]="commerce() ? listing.imageUrl : (listing.sellerImage || listing.imageUrl)"
                      [mediaSizes]="commerce() ? listing.imageSizes : (listing.sellerImageSizes || listing.imageSizes)"
                      [alt]="commerce() ? listing.title : listing.sellerName"
                      displaySizes="(max-width: 800px) 88vw, 330px"
                    />
                  }
                  @if (isFortuneBrand() || !commerce() || site.config().capabilities.marketplace) { <span class="listing-status-badge" [ngClass]="statusPositionClass('online')" [class.status-online]="isListingOnline(listing)" [class.status-duty]="listingStatusKind(listing) === 'duty'" [class.status-product-seller]="commerce() && !isFortuneBrand()" [class.status-reader]="isFortuneBrand() || !commerce()" [class.status-reading]="listingStatusKind(listing) === 'reading'" [class.status-offline]="listingStatusKind(listing) === 'offline'" [class.status-today]="listingStatusKind(listing) === 'today'" [title]="isFortuneBrand() || !commerce() ? fortunePresenceLabel(listing) : sellerLabel(listing)" [attr.aria-label]="isFortuneBrand() || !commerce() ? fortunePresenceLabel(listing) : sellerLabel(listing)">
                    @if (isListingOnline(listing)) { <i class="seller-online-dot"></i> }
                    <b>{{ isFortuneBrand() || !commerce() ? fortunePresenceLabel(listing) : sellerLabel(listing) }}</b>
                  </span> }
                  @if (isFortuneBrand() && fortuneStatusLabel(listing); as statusLabel) {
                    <span class="fortune-status-badge" [ngClass]="statusPositionClass('fortune')" [class.status-reading-bottom]="listingStatusKind(listing) === 'reading'" [class.status-offline-bottom]="listingStatusKind(listing) === 'offline'" [class.status-today-bottom]="listingStatusKind(listing) === 'today'">{{ statusLabel }}</span>
                  }
                  @if (commerce() && listing.images.length > 1) {
                    <div class="image-dots" [attr.aria-label]="productImagesLabel()">
                      @for (image of listing.images; track $index) { <i></i> }
                    </div>
                  }
                  @if (favoriteVisible()) { <app-favorite-collection class="listing-favorite" [ngClass]="favoritePositionClass()" [listingID]="listing.id" [listingSlug]="listing.slug" /> }
                </div>
                <div class="card-body">
                  @if (isFortuneBrand()) {
                    <div class="mobile-fortune-statuses">
                      <span class="listing-status-badge" [class.status-online]="isListingOnline(listing)" [class.status-duty]="listingStatusKind(listing) === 'duty'" [class.status-reading]="listingStatusKind(listing) === 'reading'" [class.status-offline]="listingStatusKind(listing) === 'offline'" [class.status-today]="listingStatusKind(listing) === 'today'">
                        @if (isListingOnline(listing)) { <i class="seller-online-dot"></i> }
                        <b>{{ fortunePresenceLabel(listing) }}</b>
                      </span>
                      @if (fortuneStatusLabel(listing); as mobileStatusLabel) {
                        <span class="fortune-status-badge" [class.status-reading-bottom]="listingStatusKind(listing) === 'reading'" [class.status-offline-bottom]="listingStatusKind(listing) === 'offline'" [class.status-today-bottom]="listingStatusKind(listing) === 'today'">{{ mobileStatusLabel }}</span>
                      }
                    </div>
                  }
                  @if (cardFieldVisible('seller') && (!commerce() || site.config().capabilities.marketplace)) { <p class="seller-name card-field" [style.order]="cardFieldOrder('seller')">{{ sellerLabel(listing) }}</p> }
                  @if (cardFieldVisible('rating') && (listing.rating > 0 || listing.reviewCount > 0)) { <div class="rating-line card-field" [style.order]="cardFieldOrder('rating')" [attr.aria-label]="'(' + (listing.rating | number: '1.2-2') + ')'">
                    <span class="rating-stars" aria-hidden="true">
                      @for (star of ratingStars(listing.rating); track $index) { <i [class.partial]="star.partial" [style.--rating-fill]="star.fill + '%'">★</i> }
                    </span>
                    <span class="rating-value">({{ listing.rating | number: '1.2-2' }})</span>
                    <span>· {{ listing.reviewCount }} {{ i18n.t('reviews') }}@if(!commerce()){ · {{ listing.orderCount }} {{ i18n.t('readings') }}}</span>
                  </div> }
                  @if (cardFieldVisible('price')) {
                    <div class="price-field card-field" [style.order]="cardFieldOrder('price')">
                      @if (listing.campaignPrice !== null && listing.campaignPrice !== undefined && listing.campaign) {
                        <strong class="campaign-price">{{ listing.campaignPrice | number: '1.0-2' }} {{ priceUnit(listing.currency) }}</strong>
                        <del>{{ listing.price | number: '1.0-2' }} {{ priceUnit(listing.currency) }}</del>
                        <span class="campaign-badge">{{ campaignLabel(listing) }}</span>
                      } @else {
                        <strong>{{ listing.price | number: '1.0-2' }} {{ priceUnit(listing.currency) }}</strong>
                        @if (listing.campaign) { <span class="campaign-badge">{{ campaignLabel(listing) }}</span> }
                      }
                    </div>
                  }
                  @if (cardFieldVisible('title')) { <h3 class="card-field" [style.order]="cardFieldOrder('title')">{{ listing.title }}</h3> }
                  @if (commerce() && activeCategory() && listingCardDescription(listing); as summary) {
                    <p class="listing-description">{{ summary }}</p>
                  }
                  @if (isFortuneBrand() && listing.categoryTitle) { <small class="fortune-listing-category">{{ listing.categoryTitle }}</small> }
                </div>
              </a>
            } @empty {
              <div class="empty-state">{{ commerce() ? commerceLabel('noProducts') : i18n.t('readersLoading') }}</div>
            }
          </div>
          @if (listMode() === 'infinite') {
            @if (loadingListings()) {
              <div class="listing-loading" role="status">{{ commerce() ? commerceLabel('productsLoading') : i18n.t('readersLoading') }}</div>
            } @else if (hasMoreListings()) {
              <div class="listing-loading listing-more"><span>{{ commerce() ? commerceLabel('moreReady') : i18n.t('readersLoading') }}</span><button type="button" class="ghost" (click)="loadMoreListings()">{{commerce()?commerceLabel('showMore'):'Daha fazla göster'}}</button></div>
            }
            <div #listingSentinel class="listing-sentinel" aria-hidden="true"></div>
          } @else {
            @if (loadingListings()) { <div class="listing-loading" role="status">{{ commerce() ? commerceLabel('productsLoading') : i18n.t('readersLoading') }}</div> }
            <nav class="theme-list-pagination" [attr.aria-label]="commerceLabel('productPages')">
              @if (listingPageNumber() > 1) {
                <a [routerLink]="[]" [queryParams]="listingPageQuery(listingPageNumber()-1)">← {{commerceLabel('previous')}}</a>
              }
              @for (number of paginationPages(); track number) {
                <a [routerLink]="[]" [queryParams]="listingPageQuery(number)" [class.active]="number===listingPageNumber()" [attr.aria-current]="number===listingPageNumber()?'page':null">{{number}}</a>
              }
              @if (hasMoreListings()) {
                <a [routerLink]="[]" [queryParams]="listingPageQuery(listingPageNumber()+1)">{{commerceLabel('next')}} →</a>
              }
            </nav>
          }
          }
          @if (activeCategory()?.description || faqs().length) {
            <article class="category-seo-content category-description-bottom">
              <h2>{{ activeCategory()?.title }}</h2>
              @if (activeCategory()?.description; as description) {
                <div [innerHTML]="description | markdownHtml" [style.max-height]="categoryDescriptionExpanded() ? 'none' : '12.5em'" [style.overflow]="'hidden'" [style.webkitMaskImage]="categoryDescriptionExpanded() ? 'none' : 'linear-gradient(#000 68%, transparent 100%)'" [style.maskImage]="categoryDescriptionExpanded() ? 'none' : 'linear-gradient(#000 68%, transparent 100%)'"></div>
                @if (description.length > 420) {
                  <button type="button" class="description-toggle" (click)="categoryDescriptionExpanded.update(value => !value)">
                    {{ categoryDescriptionExpanded() ? categoryDescriptionLessLabel() : categoryDescriptionMoreLabel() }}
                  </button>
                }
              }
              @if (relatedCategories().length) {
                <nav class="category-related-links" [attr.aria-label]="activeCategory()?.title">
                  <div>
                    @for (category of relatedCategories(); track category.id) {
                      <a [routerLink]="i18n.routePath('category', { slug: category.slug })">{{ category.title }}</a>
                    }
                  </div>
                </nav>
              }
              @if (faqs().length) {
                <div class="category-faqs">
                  <h3>{{ i18n.t('faq') }}</h3>
                  @for (faq of faqs(); track faq.id) {
                    <details>
                      <summary>{{ faq.title }}</summary>
                      <p>{{ faq.content }}</p>
                    </details>
                  }
                </div>
              }
            </article>
          }
        </div>
      }

      @if (!commerce() && !activeCategory() && rootSeoCategory(); as rootCategory) {
        <article class="fortune-root-seo">
          <h2>{{ rootCategory.title }}</h2>
          @if (rootCategory.description) {
            <p>{{ rootCategory.description }}</p>
          }
          @if (faqs().length) {
            <div class="category-faqs root-faqs">
              <h3>{{ i18n.t('faq') }}</h3>
              @for (faq of faqs(); track faq.id) {
              <details>
                <summary>{{ faq.title }}</summary>
                <p>{{ faq.content }}</p>
              </details>
            }
            </div>
          }
        </article>
      }
    </section>
  `,
  styleUrl: './fortune-widget.component.scss'
})
export class FortuneWidgetComponent implements OnDestroy {
  private readonly fortune = inject(FortuneService);
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  protected productImagesLabel(): string {
    return this.i18n.lang() === 'tr' ? 'Ürün görselleri'
      : this.i18n.lang() === 'de' ? 'Produktbilder'
      : this.i18n.lang() === 'es' ? 'Imágenes del producto'
      : this.i18n.lang() === 'fr' ? 'Images du produit'
      : 'Product images';
  }
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  private readonly themeLists = inject(ThemeListExperienceService);
  protected readonly categories = signal<FortuneCategory[]>([]);
  protected readonly listings = signal<FortuneListing[]>([]);
  protected readonly featureFacets = signal<FortuneFeatureFacet[]>([]);
  protected readonly hasPhysicalListings = signal(false);
  protected readonly listingSearch = signal('');
  protected readonly minPrice = signal('');
  protected readonly maxPrice = signal('');
  protected readonly freeShipping = signal(false);
  protected readonly campaignOnly = signal(false);
  protected readonly listingSort = signal('score');
  protected readonly filtersOpen = signal(false);
  protected readonly selectedSellerID = signal<number | null>(null);
  private readonly selectedFeatureValues = signal<Record<string, number[]>>({});
  protected readonly loadingListings = signal(false);
  protected readonly hasMoreListings = signal(false);
  protected readonly listingTotal = signal(0);
  private listingPage = 0;
  private listingLimit = this.themeLists.get('collection').pageSize;
  private listingObserver?: IntersectionObserver;
  private routeFilterKey = '';
  private loadRequestID = 0;
  protected readonly activeSlug = signal('');
  protected readonly categoriesExpanded = signal(false);
  protected readonly categoryDescriptionExpanded = signal(false);
  protected readonly categoryLimit = 8;
  protected readonly listMode = computed(() => this.themeLists.get('collection').mode);
  protected readonly listingPageNumber = signal(1);
  protected readonly paginationPages = computed(() => {const totalPages=Math.max(1,Math.ceil(this.listingTotal()/this.listingLimit));const current=this.listingPageNumber();const start=Math.max(1,Math.min(current-2,totalPages-4));return Array.from({length:Math.min(5,totalPages)},(_,index)=>start+index)});
  protected readonly activeCategory = computed(() => this.categories().find((item) => item.slug === this.activeSlug()) ?? null);
  protected readonly categoryTrail = computed(() => {
    const active = this.activeCategory();
    if (!active) return [];
    const byID = new Map(this.categories().map((item) => [item.id, item]));
    const trail: FortuneCategory[] = [];
    const seen = new Set<number>();
    let current: FortuneCategory | undefined = active;
    while (current && !seen.has(current.id)) {
      trail.unshift(current);
      seen.add(current.id);
      current = current.parentID
        ? (byID.get(current.parentID) ?? this.categories().find((item) => item.versionID === current?.parentID))
        : undefined;
    }
    return trail;
  });
  protected readonly breadcrumbCategories = computed(() => {
    const trail = this.categoryTrail();
    return this.activeCategory() ? trail.slice(0, -1) : trail;
  });
  protected readonly rootCategories = computed(() => this.categories().filter((item) => !item.parentID));
  protected readonly entryCategories = computed(() => {
    const roots = this.rootCategories();
    return roots.length === 1 ? this.categories().filter((item) => this.isChildOf(item, roots[0])) : roots;
  });
  protected readonly childCategories = computed(() => {
    const active = this.activeCategory();
    return active ? this.categories().filter((item) => this.isChildOf(item, active)) : [];
  });
  protected readonly visibleCategories = computed(() => this.activeCategory() ? this.childCategories() : this.entryCategories());
  protected readonly displayCategories = computed(() => this.categoriesExpanded() ? this.visibleCategories() : this.visibleCategories().slice(0, this.categoryLimit));
  protected readonly commerce = computed(() => this.site.enabled('commerce') && !this.site.experience('fortune'));
  protected priceUnit(currency: string | null | undefined): string { const lang=this.i18n.lang(); return String(currency||'TRY').toUpperCase()==='CREDIT' ? ({tr:'Kredi',en:'credits',de:'Guthaben',es:'créditos',fr:'crédits'} as Record<string,string>)[lang] || 'credits' : String(currency||'TRY'); }
  protected readonly faqs = signal<FortuneFaq[]>([]);
  protected readonly rootSeoCategory = computed(() => this.rootCategories()[0] ?? null);
  protected readonly relatedCategories = computed(() => {
    const active = this.activeCategory();
    if (!active) return [];
    const parentID = Number(active.parentID ?? 0);
    return this.categories()
      .filter((item) => item.id !== active.id && Number(item.parentID ?? 0) === parentID)
      .slice(0, 6);
  });
  protected readonly sellerFacets = computed(() => {
    const counts = new Map<number, { id: number; name: string; count: number }>();
    for (const listing of this.listings()) {
      const current = counts.get(listing.sellerID) ?? { id: listing.sellerID, name: this.sellerLabel(listing), count: 0 };
      current.count += 1;
      counts.set(listing.sellerID, current);
    }
    return [...counts.values()].filter((item) => item.id > 0);
  });

  protected cardTemplate() {
    // Fal Deryasi cards are still product cards. The seller/fortune labels are
    // overlays; using the seller template here ignored the admin product order.
    const templateType = this.commerce() || this.isFortuneBrand() ? 'product' : 'seller';
    const configured = this.site.config().listingTemplates?.[templateType];
    return configured ?? defaultListingTemplates[templateType];
  }

  protected cardTemplateType(): 'product' | 'seller' {
    return this.commerce() || this.isFortuneBrand() ? 'product' : 'seller';
  }

  protected cardFieldVisible(field: ListingTemplateField): boolean {
    if (this.isFortuneBrand()) return true;
    if (field === 'description') return false;
    if (field === 'seller' && this.commerce() && !this.site.config().capabilities.marketplace) return false;
    return this.cardTemplate().fields.includes(field);
  }

  protected cardFieldOrder(field: ListingTemplateField): number {
    const index = this.cardTemplate().fields.indexOf(field);
    if (this.isFortuneBrand() && index < 0) return ['seller', 'rating', 'price', 'title', 'description'].indexOf(field);
    return index < 0 ? 99 : index;
  }

  protected statusPositionClass(kind: 'online' | 'fortune'): string {
    if (kind === 'fortune' && this.isFortuneBrand()) return 'template-status-bottom-left';
    const position: ListingTemplatePosition = kind === 'online'
      ? this.cardTemplate().onlineStatusPosition
      : this.cardTemplate().fortuneStatusPosition;
    return `template-status-${position}`;
  }

  protected favoriteVisible(): boolean { return this.site.enabled('favorites') && this.cardTemplate().showFavorite !== false; }
  protected favoritePositionClass(): string { return `template-favorite-${this.cardTemplate().favoritePosition ?? 'bottom-right'}`; }

  constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, query]) => {
      const nextKey = `${params.get('slug') ?? ''}?${query.toString()}`;
      if (nextKey === this.routeFilterKey) return;
      this.routeFilterKey = nextKey;
      this.activeSlug.set(params.get('slug') ?? '');
      this.categoryDescriptionExpanded.set(false);
      this.restoreFilterQuery(query);
      // A locale switch keeps the component instance alive. Drop localized
      // data so the next load cannot compare a translated URL with stale slugs.
      this.categories.set([]);
      this.listings.set([]);
      this.featureFacets.set([]);
      this.hasPhysicalListings.set(false);
      this.selectedFeatureValues.set({});
      this.loadingListings.set(false);
      const requestID = ++this.loadRequestID;
      void this.load(requestID);
    });
  }

  @ViewChild('listingSentinel')
  private set listingSentinel(element: ElementRef<HTMLElement> | undefined) {
    this.listingObserver?.disconnect();
    if (!element || typeof IntersectionObserver === 'undefined') return;
    this.listingObserver = new IntersectionObserver((entries) => {
      if (this.listMode()==='infinite' && entries.some((entry) => entry.isIntersecting)) {
        void this.loadMoreListings();
      }
    }, { rootMargin: '600px 0px' });
    this.listingObserver.observe(element.nativeElement);
  }

  ngOnDestroy(): void {
    this.listingObserver?.disconnect();
  }

  protected childCount(parentID: number): number {
    const parent = this.categories().find((item) => item.id === parentID);
    return parent ? this.categories().filter((item) => this.isChildOf(item, parent)).length : 0;
  }

  protected categoryDescriptionMoreLabel(): string {
    return this.commerce() ? (this.i18n.lang() === 'tr' ? 'Devamını oku' : this.i18n.lang() === 'de' ? 'Mehr lesen' : this.i18n.lang() === 'es' ? 'Leer más' : this.i18n.lang() === 'fr' ? 'Lire la suite' : 'Read more') : this.i18n.t('continue');
  }

  protected categoryDescriptionLessLabel(): string {
    return this.commerce() ? (this.i18n.lang() === 'tr' ? 'Daha az göster' : this.i18n.lang() === 'de' ? 'Weniger anzeigen' : this.i18n.lang() === 'es' ? 'Mostrar menos' : this.i18n.lang() === 'fr' ? 'Afficher moins' : 'Show less') : this.i18n.t('continue');
  }
  protected commerceLabel(key: string): string {
    const values: Record<string, Record<string, string>> = {
      en: { products:'Products',catalog:'Catalog',catalogTitle:'Products & adult furniture',catalogLead:'Explore beds, crosses, benches, chairs, cages and modular furniture. Compare the collections, then open a product for its current price and configuration.',viewProducts:'View products',showLess:'Show fewer collections',showAll:'View all collections',compare:'Compare models',allCategories:'All categories',filter:'Filter',filters:'Filters',closeFilters:'Close filters',productFilters:'Product filters',search:'Search',searchProducts:'Search products',minPrice:'Min. price',maxPrice:'Max. price',freeShipping:'Free shipping',campaignProducts:'Sale products',seller:'Seller',noProducts:'No products are available in this collection yet.',productsLoading:'Products are loading.',moreReady:'More products are available.',showMore:'Show more',productPages:'Product pages',previous:'Previous',next:'Next' },
      tr: { products:'Ürünler',catalog:'Katalog',catalogTitle:'Ürünler ve koleksiyonlar',catalogLead:'Ürün ailelerini inceleyin, koleksiyonları karşılaştırın ve güncel fiyat ile seçenekler için ürünü açın.',viewProducts:'Ürünleri gör',showLess:'Daha az kategori',showAll:'Tüm kategorileri gör',compare:'Modelleri karşılaştır',allCategories:'Tüm kategoriler',filter:'Filtrele',filters:'Filtreler',closeFilters:'Filtreleri kapat',productFilters:'Ürün filtreleri',search:'Arama',searchProducts:'Ürün ara',minPrice:'Min. fiyat',maxPrice:'Maks. fiyat',freeShipping:'Ücretsiz kargo',campaignProducts:'Kampanyalı ürünler',seller:'Satıcı',noProducts:'Bu koleksiyonda henüz ürün yok.',productsLoading:'Ürünler yükleniyor.',moreReady:'Daha fazla ürün var.',showMore:'Daha fazla göster',productPages:'Ürün sayfaları',previous:'Önceki',next:'Sonraki' }
    };
    const lang = this.i18n.lang();
    return (values[lang] ?? values['en'])[key] ?? values['en'][key] ?? key;
  }

  protected categoryIntro(): string {
    const description = String(this.activeCategory()?.description ?? '')
      .replace(/\\n/g, '\n')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[>*_`]/g, '')
      .trim();
    const first = description.split(/\n\s*\n/).find((part) => part.trim().length > 40) ?? description;
    const sentence = first.match(/^.{40,220}?[.!?](?:\s|$)/)?.[0]?.trim() ?? first;
    if(sentence.length<=220)return sentence;
    const clipped=sentence.slice(0,217);const boundary=clipped.lastIndexOf(' ');
    return `${clipped.slice(0,boundary>140?boundary:217).trimEnd()}…`;
  }
  protected categoryModelAnchor(): string | null {
    const slug=String(this.activeCategory()?.slug||'').toLowerCase();
    return slug==='bdsm-beds'?'bed-models':slug==='bdsm-crosses'?'cross-models':slug==='bdsm-benches'?'bench-models':null;
  }
  protected categoryModelHref(): string | null {
    const category=this.activeCategory(),anchor=this.categoryModelAnchor();
    return category&&anchor?`${this.i18n.routePath('category',{slug:category.slug})}#${anchor}`:null;
  }
  protected scrollToCategoryModels(event: Event): void {
    const anchor=this.categoryModelAnchor(),href=this.categoryModelHref();
    if(!anchor||!href||typeof document==='undefined')return;
    const target=document.getElementById(anchor);
    if(!target)return;
    event.preventDefault();
    if(typeof history!=='undefined')history.pushState(history.state,'',href);
    target.scrollIntoView({behavior:'smooth',block:'start'});
  }

  private isChildOf(item: FortuneCategory, parent: FortuneCategory): boolean {
    const parentID = Number(item.parentID ?? 0);
    return parentID > 0 && (parentID === parent.id || parentID === Number(parent.versionID ?? 0));
  }

  protected sellerLabel(listing: FortuneListing): string {
    return this.commerce() ? (listing.sellerShopName || listing.sellerName) : listing.sellerName;
  }

  protected listingCardDescription(listing: FortuneListing): string {
    const clean = String(listing.description ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[>*_`]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!clean) return '';
    const sentence = clean.match(/^.{35,180}?[.!?](?:\s|$)/)?.[0]?.trim() ?? clean;
    return sentence.length > 150 ? `${sentence.slice(0, 147).trimEnd()}…` : sentence;
  }

  protected isFortuneBrand(): boolean {
    return this.site.experience('fortune');
  }

  protected isNewReader(listing: FortuneListing): boolean {
    if (listing.isNewReader) return true;
    const status = [listing.statusLabel, listing.statusCode, listing.liveStatus, listing.highlightText]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toLowerCase();
    return /(^|\s)(new|yeni)(\s+(seller|reader|falcı|falci))?(\s|$)/.test(status);
  }

  protected fortuneStatusLabel(listing: FortuneListing): string {
    return String(listing.rankReminderName || listing.rankTitle || (this.isNewReader(listing) ? 'Yeni Falcı' : 'Falcı')).trim();
  }

  protected fortunePresenceLabel(listing: FortuneListing): string {
    const presence = String(listing.presenceStatus ?? listing.liveStatus ?? '').trim().toLowerCase();
    if (presence === 'on_duty' || presence === 'nobetci' || presence === 'nöbetçi') return 'Nöbetçi';
    if (presence === 'reading' || presence === 'fortune' || presence === 'falda' || presence === 'busy' || presence === 'meşgul') return 'Falda';
    if (presence === 'intraday' || presence === 'today' || presence.includes('gün içi') || presence.includes('gun ici')) return 'Çevrimiçi';
    if (this.isListingOnline(listing)) return this.i18n.t('online');
    return 'Çevrimdışı';
  }

  protected isListingOnline(listing: FortuneListing): boolean {
    const status = String(listing.liveStatus ?? '').trim().toLowerCase();
    const presenceStatuses = ['online', 'available', 'active', 'live', 'çevrimiçi', 'cevrimici', 'müsait', 'musait'];
    return status ? presenceStatuses.includes(status) : listing.online === true;
  }

  protected listingStatusLabel(listing: FortuneListing): string {
    const status = String(listing.statusLabel ?? listing.liveStatus ?? '').trim().toLowerCase();
    if (this.isListingOnline(listing)) return this.i18n.t('online');
    return listing.liveStatus || this.i18n.t('reader');
  }

  protected listingStatusKind(listing: FortuneListing): 'reading' | 'offline' | 'today' | 'duty' | 'default' {
    const status = String(listing.presenceStatus ?? listing.liveStatus ?? '').trim().toLowerCase();
    if (['reading', 'fortune', 'falda', 'fal bakıyor', 'falda bakıyor', 'busy', 'meşgul'].includes(status)) return 'reading';
    if (['offline', 'inactive', 'çevrimdışı', 'cevrimdisi'].includes(status)) return 'offline';
    if (['on_duty', 'nobetci', 'nöbetçi'].includes(status)) return 'duty';
    if (['intraday', 'today'].includes(status) || status.includes('gün içi') || status.includes('gun ici')) return 'default';
    return 'default';
  }

  protected ratingStars(value: number): Array<{ partial: boolean; fill: number }> {
    const rating = Math.max(0, Math.min(5, Number(value) || 0));
    return Array.from({ length: 5 }, (_, index) => {
      const fill = Math.max(0, Math.min(100, (rating - index) * 100));
      return { partial: fill > 0 && fill < 100, fill };
    });
  }

  protected isPhysicalListing(listing: FortuneListing): boolean {
    return ['physical', 'physical_product', 'physical-product'].includes(String(listing.listingType ?? '').trim().toLowerCase());
  }

  protected isFeatureSelected(featureID: number, valueID: number): boolean {
    return (this.selectedFeatureValues()[String(featureID)] ?? []).includes(valueID);
  }

  protected toggleFeatureValue(featureID: number, valueID: number): void {
    const key = String(featureID);
    const current = this.selectedFeatureValues()[key] ?? [];
    const values = current.includes(valueID) ? current.filter((id) => id !== valueID) : [...current, valueID];
    const next = { ...this.selectedFeatureValues() };
    if (values.length) next[key] = values;
    else delete next[key];
    this.selectedFeatureValues.set(next);
    void this.syncFilterQuery();
  }

  protected toggleSeller(sellerID: number): void {
    this.selectedSellerID.set(this.selectedSellerID() === sellerID ? null : sellerID);
    void this.syncFilterQuery();
  }

  protected applyListingFilters(): void {
    this.filtersOpen.set(false);
    void this.syncFilterQuery();
  }

  private restoreFilterQuery(query: import('@angular/router').ParamMap): void {
    this.listingSearch.set(query.get('search') ?? '');
    this.minPrice.set(query.get('min') ?? '');
    this.maxPrice.set(query.get('max') ?? '');
    this.freeShipping.set(query.get('shipping') === 'free');
    this.campaignOnly.set(query.get('campaign') === '1');
    const seller = Number(query.get('seller') ?? 0);
    this.selectedSellerID.set(seller > 0 ? seller : null);
    const next: Record<string, number[]> = {};
    for (const key of query.keys) {
      if (!key.startsWith('feature_')) continue;
      const values = String(query.get(key) ?? '').replace(/^\[|\]$/g, '').split(',')
        .map((item) => Number(item.trim())).filter((item) => item > 0);
      if (values.length) next[key.slice(8)] = values;
    }
    this.selectedFeatureValues.set(next);
  }

  private async syncFilterQuery(): Promise<void> {
    const query: Record<string, string | null> = {
      search: this.listingSearch().trim() || null,
      min: this.minPrice().trim() || null,
      max: this.maxPrice().trim() || null,
      shipping: this.freeShipping() ? 'free' : null,
      campaign: this.campaignOnly() ? '1' : null,
      seller: this.selectedSellerID() ? String(this.selectedSellerID()) : null
    };
    for (const facet of this.featureFacets()) {
      const values = this.selectedFeatureValues()[String(facet.id)] ?? [];
      if (!values.length) continue;
      const key = facet.key || `feature_${facet.id}`;
      const labels = values.map((id) => facet.values.find((item) => item.id === id)?.title ?? String(id))
        .map((item) => item.toLocaleLowerCase('en-US').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
      query[key] = `[${labels.join(',')}]`;
      query[`feature_${facet.id}`] = `[${values.join(',')}]`;
    }
    await this.router.navigate([], { relativeTo: this.route, queryParams: query, replaceUrl: true });
  }

  protected campaignLabel(listing: FortuneListing): string {
    const campaign = listing.campaign;
    if (!campaign) return '';
    if (campaign.buyQuantity && campaign.payQuantity) return `${campaign.buyQuantity} al ${campaign.payQuantity} öde`;
    if (campaign.discountType === 'percent' && campaign.discountValue !== null) return `%${campaign.discountValue} indirim`;
    if (campaign.discountValue !== null) return `${campaign.discountValue} ${listing.currency} indirim`;
    return campaign.title;
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    if (this.listMode()!=='infinite' || !this.activeCategory() || this.loadingListings() || !this.hasMoreListings()) return;
    const remaining = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
    if (remaining < 520) void this.loadMoreListings();
  }

  private async load(requestID = this.loadRequestID): Promise<void> {
    const categories = this.categories().length ? this.categories() : await this.fortune.categories();
    if (requestID !== this.loadRequestID) return;
    this.categories.set(categories);

    const active = this.activeCategory();
    if (this.activeSlug() && !active) {
      this.faqs.set([]);
      this.seo.setNotFound(
        this.commerce() ? 'Ürün kategorisi bulunamadı' : 'Fal kategorisi bulunamadı',
        this.commerce() ? 'Aradığın ürün kategorisi bulunamadı.' : 'Aradığın fal kategorisi bulunamadı.'
      );
      void this.router.navigateByUrl('/404');
      return;
    }

    const requestedPage = Math.max(1, Number(this.route.snapshot.queryParamMap.get('page') ?? 1));
    const seededListings = this.fortune.seededListingSnapshot({
      category: active?.slug,
      categoryID: active?.id,
      limit: this.listingLimit,
      page: requestedPage - 1,
      sort: active ? this.listingSort() : 'new',
    });
    if (seededListings) {
      this.listings.set(seededListings.items);
      this.listingPage = seededListings.page;
      this.listingPageNumber.set(seededListings.page + 1);
      this.listingTotal.set(seededListings.total);
      this.hasMoreListings.set(seededListings.hasMore);
      this.featureFacets.set(seededListings.features);
    }

    const seoCategory = active ?? this.rootSeoCategory();
    this.faqs.set(seoCategory ? await this.fortune.categoryFaqs(seoCategory.id) : []);
    if (requestID !== this.loadRequestID) return;

    if (!active) {
      if (!seededListings) {
        const result = await this.fortune.listingPage({ limit: this.listingLimit, page: requestedPage - 1, sort: 'new' });
        if (requestID !== this.loadRequestID) return;
        this.listings.set(result.items);
        this.listingPage = result.page;
        this.listingPageNumber.set(result.page + 1);
        this.listingTotal.set(result.total);
        this.hasMoreListings.set(result.hasMore);
      }
      this.applyRootSeo();
      return;
    }

    this.applyCategorySeo(active);

    // Parent categories are navigation nodes. Products are loaded only after
    // the visitor reaches a leaf category.
    if (this.childCategories().length) {
      this.listings.set([]);
      this.hasMoreListings.set(false);
      return;
    }

    if (seededListings) {
      this.applyCategorySeo(active);
      return;
    }

    this.listingPage = 0;
    this.listings.set([]);
    this.featureFacets.set([]);
    await this.loadMoreListings(true, requestID, requestedPage - 1);
    if (requestID !== this.loadRequestID) return;
    this.applyCategorySeo(active);
  }

  private applyRootSeo(): void {
    const category = this.rootSeoCategory();
    if (!category) return;
    const path = this.i18n.routePath('fortune');
    const title = this.commerce() ? `${this.commerceLabel('catalogTitle')} | ${this.site.config().name}` : (category.metaTitle?.trim() || category.title);
    const description = this.commerce() ? this.commerceLabel('catalogLead') : (category.metaDescription?.trim() || category.description?.trim() || category.title);
    const itemList = this.entryCategories().map((category, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: category.title,
      url: this.absoluteSiteUrl(this.i18n.routePath('category', { slug: category.slug }))
    }));
    const schema: Record<string, unknown>[] = [{
      '@type': 'CollectionPage',
      '@id': `${this.absoluteSiteUrl(path)}#collection`,
      name: title,
      description,
      primaryImageOfPage: category.imageUrl ? { '@type': 'ImageObject', url: this.absoluteMediaUrl(category.imageUrl) } : undefined,
      mainEntity: { '@type': 'ItemList', itemListElement: itemList }
    }];
    if (this.faqs().length) schema.push(this.faqSchema(this.faqs()));

    this.seo.setPage({
      title,
      description,
      image: category.imageUrl,
      path,
      modifiedAt: this.site.config().lastUpdatedAt,
      breadcrumbs: [
        { name: this.i18n.t('home'), path: '/' },
        { name: this.commerce() ? this.commerceLabel('products') : category.title, path }
      ],
      schema
    });
  }

  private applyCategorySeo(category: FortuneCategory): void {
    const path = this.i18n.routePath('category', { slug: category.slug });
    const title = category.metaTitle?.trim() || category.title;
    const description = category.metaDescription?.trim() || category.description?.trim() || category.title;
    const collection = this.childCategories().length ? this.childCategories() : this.listings();
    const itemList = collection.map((item, index) => {
      const isCategory = 'parentID' in item;
      const itemPath = isCategory
        ? this.i18n.routePath('category', { slug: item.slug })
        : this.i18n.routePath('listing', { slug: item.slug });
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        url: this.absoluteSiteUrl(itemPath)
      };
    });
    const schema: Record<string, unknown>[] = [{
      '@type': 'CollectionPage',
      '@id': `${this.absoluteSiteUrl(path)}#collection`,
      name: title,
      description,
      primaryImageOfPage: category.imageUrl ? { '@type': 'ImageObject', url: this.absoluteMediaUrl(category.imageUrl) } : undefined,
      mainEntity: { '@type': 'ItemList', numberOfItems: itemList.length, itemListElement: itemList }
    }];
    if (this.faqs().length) schema.push(this.faqSchema(this.faqs()));

    this.seo.setPage({
      title,
      description,
      image: category.imageUrl,
      path,
      modifiedAt: this.site.config().lastUpdatedAt,
      breadcrumbs: [
        { name: this.i18n.t('home'), path: '/' },
        { name: this.commerce() ? this.commerceLabel('products') : this.i18n.t('fortune'), path: this.i18n.routePath('fortune') },
        ...this.breadcrumbCategories().map((item) => ({ name: item.title, path: this.i18n.routePath('category', { slug: item.slug }) }))
      ],
      schema
    });
  }

  private faqSchema(faqs: FortuneFaq[]): Record<string, unknown> {
    return {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.title,
        acceptedAnswer: { '@type': 'Answer', text: faq.content }
      }))
    };
  }

  private absoluteSiteUrl(path: string): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${this.site.config().apiBaseUrl.replace(/\/+$/, '')}${normalized}`;
  }

  private absoluteMediaUrl(path: string): string {
    const normalized = path.replace('[size]', 'b');
    return /^https?:\/\//i.test(normalized)
      ? normalized
      : `${this.site.config().apiBaseUrl.replace(/\/+$/, '')}/${normalized.replace(/^\/+/, '')}`;
  }

  protected async loadMoreListings(reset = false, requestID = this.loadRequestID, requestedPage?:number): Promise<void> {
    const active = this.activeCategory();
    if (requestID !== this.loadRequestID || !active || this.loadingListings() || (!reset && !this.hasMoreListings())) return;
    this.loadingListings.set(true);
    try {
      const nextPage = requestedPage??(reset ? 0 : this.listingPage + 1);
      const result = await this.fortune.listingPage({
        category: active.slug,
        categoryID: active.id,
        limit: this.listingLimit,
        page: nextPage,
        search: this.listingSearch() || undefined,
        minPrice: this.minPrice() ? Number(this.minPrice()) : undefined,
        maxPrice: this.maxPrice() ? Number(this.maxPrice()) : undefined,
        freeShipping: this.freeShipping(),
        sellerID: this.selectedSellerID(),
        optionFilter: this.selectedFeatureValues(),
        campaignOnly: this.campaignOnly()
        ,sort: this.listingSort()
      });
      if (requestID !== this.loadRequestID) return;
      const current = reset || this.listMode()==='pagination' ? [] : this.listings();
      const seen = new Set(current.map((item) => item.id));
      // The API scopes the query by category, but older marketplace responses
      // can omit categorySlug or expose a category/version ID in a different
      // field. Accept a result only when it belongs to the current category;
      // never render the previous category's products under the new URL.
      const activeSlug = active.slug.trim().toLocaleLowerCase();
      const scopedItems = result.items.filter((item) => {
        const itemSlug = String(item.categorySlug ?? '').trim().toLocaleLowerCase();
        if (itemSlug) return itemSlug === activeSlug;
        // The legacy marketplace response does not always include the
        // category slug. In that case the backend categoryID filter is the
        // source of truth; accepting the row also preserves child-category
        // results when a parent category is requested.
        return true;
      });
      const visibleItems = this.commerce() ? scopedItems : scopedItems.filter((item) => item.sellerImage !== null && item.sellerImage !== item.imageUrl);
      const fresh = visibleItems.filter((item) => !seen.has(item.id));
      this.listings.set([...current, ...fresh]);
      if (reset) {
        const physical = fresh.some((item) => this.isPhysicalListing(item));
        this.hasPhysicalListings.set(physical);
        if (!physical) this.freeShipping.set(false);
      } else if (!this.hasPhysicalListings() && fresh.some((item) => this.isPhysicalListing(item))) {
        this.hasPhysicalListings.set(true);
      }
      this.listingPage = result.page;
      this.listingPageNumber.set(result.page+1);
      this.listingTotal.set(result.total);
      if (reset || !this.featureFacets().length) this.featureFacets.set(result.features);
      const hasMore = result.hasMore && (fresh.length > 0 || result.items.length > 0);
      this.hasMoreListings.set(hasMore);
      if (
        hasMore
        && typeof document !== 'undefined'
        && typeof window !== 'undefined'
        && document.documentElement.scrollHeight <= window.innerHeight + 520
      ) {
        window.setTimeout(() => void this.loadMoreListings(false, requestID), 0);
      }
    } finally {
      if (requestID === this.loadRequestID) this.loadingListings.set(false);
    }
  }

  protected listingPageQuery(page:number):Record<string,unknown>{return{...this.route.snapshot.queryParams,page:page>1?page:null}}
}
